import { Component, Input, OnChanges, SimpleChanges, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { Defect, DefectMetadata, DefectSeverity, DialogType } from '@jms/model';
import { ConfirmService } from '@jms/ui/ui.confirm';
import { ArrayUtility, ObservableInput } from '@jms/utils/util.redux';
import { StateComponent } from '@jms/utils/util.state.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as Enumerable from 'linq';
import { combineLatest, lastValueFrom, map, Observable, of, switchMap, tap } from 'rxjs';
import { DefectService } from '../../services/defect.service';
import { NgIf, AsyncPipe } from '@angular/common';
import { DefectListComponent } from '../defect.list/defect.list.component';
import { SideDialogComponent } from '@jms/ui/ui.side.dialog';
import { DefectCreatorComponent } from '../defect.creator/defect.creator.component';

interface DefectDashboardComponentVM {
  dialog: DialogType;
  defects: Defect[];
  selectedDefect: Defect;
  metadata: DefectMetadata;
}

const initialState = {
  dialog: DialogType.None,
  defects: [],
  selectedDefect: null,
  metadata: null
} as DefectDashboardComponentVM;


@UntilDestroy()
@Component({
    selector: 'defect-dashboard',
    templateUrl: './defect.dashboard.component.html',
    styleUrls: ['./defect.dashboard.component.less'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgIf, DefectListComponent, SideDialogComponent, DefectCreatorComponent, AsyncPipe]
})
export class DefectDashboardComponent extends StateComponent<DefectDashboardComponentVM> implements OnChanges {

  @ObservableInput() @Input('data') public data$: Observable<DefectMetadata>;
  @Input() public refresh: boolean;


  public DialogType = DialogType;

  constructor(
    private defectService: DefectService,
    private confirmService: ConfirmService
  ) {
    super(initialState);

    combineLatest([
      this.data$
    ])
      .pipe(
        untilDestroyed(this),
        switchMap(([metadata]: [DefectMetadata]) => {

          if (!metadata?.type) return of([]);

          this.setState({ metadata })

          return this.defectService.getDefects(metadata);

        }),
        tap(defects => { this.setState({ defects }) })
      )
      .subscribe();
  }

  public async ngOnChanges(changes: SimpleChanges) {

    if (changes?.refresh && this.refresh) {

      const defects = await lastValueFrom(this.defectService.getDefects(this.vm.metadata));

      this.setState({ defects })
    }
  }

  public onOpenDialog(dialog: DialogType) {

    if (dialog === DialogType.New) {
      this.getSelectedDefect();
    }

    this.setState({ dialog: dialog })

  }

  public async onSaveDefect(defect: Defect) {

    const result = await lastValueFrom(this.defectService.onSave(defect));

    const defects = ArrayUtility.tryReplace(this.vm.defects, result, x => x._id === result._id);

    this.setState({
      dialog: DialogType.None,
      defects: defects
    })
  }

  private getSelectedDefect() {

    const { job, workOrder, timezone } = this.vm.metadata;

    const defect = {
      _id: null,
      job: job,
      workOrder: workOrder,
      code: null,
      defectNumber: null,
      raisedBy: null,
      raisedAt: new Date(),
      rectifiedBy: null,
      rectifiedAt: new Date(),
      severity: DefectSeverity.Low,
      description: null,
      resolve: false,
      causedBy: null,
      defectEvidence: [],
      rectification: [],
      resolution: null,
      timezone: timezone
    }

    this.setState({
      selectedDefect: defect
    })

  }

  public async onEdit(defectId: string) {

    const defect = await lastValueFrom(this.defectService.getById(defectId));

    this.setState({
      selectedDefect: defect,
      dialog: DialogType.Edit
    });

  }

  public async onDelete(defectId: string) {

    if (!defectId)
      return;

    const confirm = await this.confirmService
      .confirm(`Delete Defect`, `Are you sure you wish to delete this defect record?`, 'danger')
      .then(confirm => {
        if (!confirm) return false;
        return true;
      });

    if (!confirm)
      return;

    const result = await lastValueFrom(this.defectService.onDelete(defectId));

    if (result?.success) {

      const defects = Enumerable.from(this.vm.defects)
        .where(x => x._id !== defectId)
        .toArray();

      this.setState({
        defects: defects
      })
    }


  }

  public async onCancel(valueChanged: boolean) {

    let cancel = true;

    if (this.vm.dialog === DialogType.Edit && valueChanged) {
      cancel = await this.confirmService.confirm(`Unsaved Changes`, `You have unsaved changes. Are you sure you wish to exit?`, 'info');
    }

    if (!cancel)
      return;

    this.setState({
      dialog: DialogType.None
    })

  }



}
